import { ContactSupportRequestState } from 'communication/types/ContactSupportRequest';
import { ChannelInfo } from 'communication/Channel';
import { Button } from '@instinet-analytics/semantic-ui-components';
import classNames from 'classnames';
import { CheckboxInput } from '@instinet-analytics/semantic-ui-components';
import React from 'react';
import uniq from 'lodash/uniq';
import { RequestInfo } from './Requests';

export interface RequestFilter {
    keys: string[];
    onlyLatestRequests: boolean;
    includeRpcChannels: boolean;
}

export interface RequestFilterProps {
    filter: RequestFilter;
    state: ContactSupportRequestState;
    onUpdateFilter: (nextFilter: RequestFilter) => void;
}

export default function RequestFilters(props: RequestFilterProps) {
    const { filter, state, onUpdateFilter } = props;

    const keys: string[] = uniq([
        ...Object.keys(state?.state?.requestProgress),
        'batchStreamRequest',
        ...getChannels(state, filter).map((c) => c.destination!),
    ]);

    const setAllKeys = () => {
        onUpdateFilter({ ...filter, keys: [] });
    };

    const toggleKey = (k: string) => {
        const nextKeys =
            filter.keys.indexOf(k) > -1
                ? filter.keys.filter((c) => c !== k)
                : [...filter.keys, k];
        onUpdateFilter({ ...filter, keys: nextKeys });
    };

    const toggleOnlyLatestRequests = () => {
        onUpdateFilter({
            ...filter,
            onlyLatestRequests: !filter.onlyLatestRequests,
        });
    };

    const toggleIncludeRpcChannels = () => {
        onUpdateFilter({
            ...filter,
            includeRpcChannels: !filter.includeRpcChannels,
        });
    };

    return (
        <div className="RequestFilters">
            <div className="keys">
                <div className="title">Keys:</div>
                <div>
                    <Button className={'primary'} onClick={setAllKeys}>
                        All
                    </Button>

                    {keys.map((k) => (
                        <Button
                            key={k}
                            className={classNames(
                                'mini',
                                (filter.keys.length === 0 ||
                                    filter.keys.indexOf(k) > -1) &&
                                    'selected',
                                'active'
                            )}
                            onClick={() => toggleKey(k)}
                        >
                            {k}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="checkboxes">
                <CheckboxInput
                    label="Only latest requests"
                    checked={filter.onlyLatestRequests}
                    onToggle={toggleOnlyLatestRequests}
                />
                <CheckboxInput
                    label="Include RPC Channels"
                    checked={filter.includeRpcChannels}
                    onToggle={toggleIncludeRpcChannels}
                />
            </div>
        </div>
    );
}

export function applyFilter(messages: RequestInfo[], filter: RequestFilter) {
    const keysFilter = (m: RequestInfo) =>
        filter.keys.length === 0 || filter.keys.indexOf(m.key) > -1;

    const latestOnlyFilter = (m: RequestInfo) =>
        !filter.onlyLatestRequests || m.isLatest;

    return messages.filter(keysFilter).filter(latestOnlyFilter);
}

export function getChannels(
    state: ContactSupportRequestState,
    filter: RequestFilter
) {
    let allChannels: ChannelInfo[] = state?.streams?.channels ?? [];
    if (!Array.isArray(allChannels)) {
        allChannels = Object.values(allChannels);
    }
    return filter.includeRpcChannels
        ? allChannels.filter((c) => isRpcChannel(c))
        : [];
}

function isRpcChannel(c: ChannelInfo) {
    return (
        c.destination === 'symbolInfo' ||
        c.destination === 'startExpertiseByOrderIdsStream'
    );
}
